import { basePath } from "@Common/constants";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import {
	AppBar,
	Box,
	Button,
	Container,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Toolbar,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface NavLink {
	label: string;
	path: string;
}

const navLinks: NavLink[] = [
	{ label: "Features", path: "/features" },
	{ label: "Pricing", path: "/pricing" },
	{ label: "About", path: "/about" },
	{ label: "Contact", path: "/contact" },
];

export const PublicNav = () => {
	const navigate = useNavigate();
	const [mobileOpen, setMobileOpen] = useState(false);

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const handleNavigation = (path: string) => {
		navigate(path);
		setMobileOpen(false);
	};

	const drawer = (
		<Box sx={{ pt: 2 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					px: 2,
					pb: 2,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Box
						component="img"
						src="/logos/nomachine.svg"
						alt="NoMachine"
						sx={{ height: 28 }}
					/>
					<Typography variant="h6" sx={{ fontWeight: 700 }}>
						NoMachine
					</Typography>
				</Box>
				<IconButton onClick={handleDrawerToggle}>
					<CloseIcon />
				</IconButton>
			</Box>
			<List>
				{navLinks.map((link) => (
					<ListItem key={link.path} disablePadding>
						<ListItemButton onClick={() => handleNavigation(link.path)}>
							<ListItemText primary={link.label} />
						</ListItemButton>
					</ListItem>
				))}
				<ListItem disablePadding>
					<ListItemButton onClick={() => handleNavigation(basePath)}>
						<ListItemText
							primary="Sign In"
							primaryTypographyProps={{ fontWeight: 600 }}
						/>
					</ListItemButton>
				</ListItem>
			</List>
		</Box>
	);

	return (
		<>
			<AppBar
				position="sticky"
				elevation={0}
				sx={{
					bgcolor: "background.paper",
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Container maxWidth="lg">
					<Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 70 } }}>
						<Box
							component="button"
							onClick={() => handleNavigation(basePath)}
							sx={{
								display: "flex",
								alignItems: "center",
								flexGrow: { xs: 1, md: 0 },
								mr: { md: 6 },
								cursor: "pointer",
								border: "none",
								background: "transparent",
								padding: 0,
								"&:hover": {
									opacity: 0.8,
								},
							}}
						>
							<Box
								component="img"
								src="/logos/nomachine.svg"
								alt="NoMachine"
								sx={{ height: 32 }}
							/>
							<Typography
								variant="h6"
								sx={{
									fontWeight: 700,
									color: "text.primary",
									fontSize: "1.25rem",
									ml: 1,
								}}
							>
								NoMachine
							</Typography>
						</Box>

						<Box
							sx={{
								flexGrow: 1,
								display: { xs: "none", md: "flex" },
								gap: 4,
								alignItems: "center",
							}}
						>
							{navLinks.map((link) => (
								<Button
									key={link.path}
									onClick={() => handleNavigation(link.path)}
									sx={{
										color: "text.secondary",
										textTransform: "none",
										fontSize: "0.95rem",
										fontWeight: 500,
										"&:hover": {
											color: "text.primary",
											bgcolor: "transparent",
										},
									}}
								>
									{link.label}
								</Button>
							))}
						</Box>

						<Button
							variant="contained"
							onClick={() => handleNavigation(basePath)}
							sx={{
								display: { xs: "none", md: "inline-flex" },
								textTransform: "none",
								px: 3,
								fontWeight: 600,
							}}
						>
							Sign In
						</Button>

						<IconButton
							color="inherit"
							aria-label="open drawer"
							edge="end"
							onClick={handleDrawerToggle}
							sx={{
								display: { md: "none" },
								color: "text.primary",
							}}
						>
							<MenuIcon />
						</IconButton>
					</Toolbar>
				</Container>
			</AppBar>

			<Drawer
				variant="temporary"
				anchor="right"
				open={mobileOpen}
				onClose={handleDrawerToggle}
				ModalProps={{
					keepMounted: true,
				}}
				sx={{
					display: { xs: "block", md: "none" },
					"& .MuiDrawer-paper": {
						boxSizing: "border-box",
						width: 280,
					},
				}}
			>
				{drawer}
			</Drawer>
		</>
	);
};
